import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPlatforms,
  getPlatformAccount,
  connectPlatform,
  refreshPlatformToken,
  disconnectPlatform,
  type ConnectPlatformInput,
  type PlatformAccountResponse,
  type PlatformListResponse,
} from '../api/platforms';

type ConnectPlatformArgs = {
  payload: ConnectPlatformInput;
  returnUrl?: string;
};

type PlatformAccountsOptions = {
  enabled?: boolean;
  userId?: string | null;
};

export const getPlatformAccountsQueryKey = (userId?: string | null) => ['platforms', 'accounts', userId ?? 'anonymous'] as const;

export const usePlatformAccounts = (options: PlatformAccountsOptions = {}) => {
  return useQuery<PlatformListResponse>({
    queryKey: getPlatformAccountsQueryKey(options.userId),
    queryFn: () => listPlatforms(),
    staleTime: 1000 * 60 * 5,
    enabled: options.enabled,
  });
};

export const usePlatformAccount = (accountId?: string | null, options: { enabled?: boolean } = {}) => {
  return useQuery<PlatformAccountResponse>({
    queryKey: ['platforms', 'account', accountId ?? 'missing'],
    queryFn: () => getPlatformAccount(accountId as string),
    enabled: Boolean(accountId) && options.enabled !== false,
    staleTime: 1000 * 30,
  });
};

export const useConnectPlatform = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: connectPlatform,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['platforms', 'accounts'] });
    },
  });
};

export const useRefreshPlatform = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) => refreshPlatformToken(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platforms', 'accounts'] });
    },
  });
};

export const useDisconnectPlatform = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }: { accountId: string }) => disconnectPlatform(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platforms', 'accounts'] });
    },
  });
};

export type { ConnectPlatformArgs };

export default usePlatformAccounts;
